import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  CAPABILITIES_MODULE_ID,
  CAPABILITY_KEYS,
  CapabilityKey,
  PageConfigView,
  getCapabilityConfig,
  clearCapabilityConfig,
} from '../api/Navigation/moduleConfig';
import { useSignalRConnection } from './useSignalRConnection';

export type CompanyCapabilities = Record<CapabilityKey, boolean>;

const ALL_ALLOWED: CompanyCapabilities = Object.freeze(
  CAPABILITY_KEYS.reduce((acc, key) => ({ ...acc, [key]: true }), {} as CompanyCapabilities),
);

export interface UseCompanyCapabilitiesResult {
  capabilities: CompanyCapabilities;
  loaded: boolean;
  can: (key: CapabilityKey) => boolean;
}

/**
 * Company capabilities: what a page allows, as opposed to what the sidebar offers.
 *
 * Deny-only. A company with no rows gets every capability, so existing tenants are unaffected. Not
 * loaded yet also means allowed, so a screen never flickers from editable to read-only.
 *
 * This is an affordance, not a security boundary. Phase 2 derives it from row ownership and enforces
 * it in the service layer.
 */
export function useCompanyCapabilities(): UseCompanyCapabilitiesResult {
  const isLoggedIn = useSelector((s: { user?: { isLoggedIn?: boolean } }) => s.user?.isLoggedIn);
  const userId = useSelector((s: { user?: { userId?: string } }) => s.user?.userId);
  const companyId = useSelector((s: { user?: { company?: { id?: number } } }) => s.user?.company?.id);
  const { subscribe, subscribeToCompanyGroup } = useSignalRConnection();

  const [denied, setDenied] = useState<Set<string>>(new Set());
  const [loaded, setLoaded] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!isLoggedIn || !userId) {
      clearCapabilityConfig();
      setDenied(new Set());
      setLoaded(true);
      return;
    }

    let cancelled = false;
    getCapabilityConfig(userId)
      .then((rows: PageConfigView[]) => {
        if (cancelled) return;
        setDenied(new Set(
          rows.filter(r => r.moduleId === CAPABILITIES_MODULE_ID && !r.isEnabled).map(r => r.pagePath),
        ));
        setLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setLoaded(true);
      });

    return () => { cancelled = true; };
  }, [isLoggedIn, userId, nonce]);

  useEffect(() => {
    if (!isLoggedIn || !companyId) return;

    subscribeToCompanyGroup(companyId).catch(() => undefined);

    const unsubscribe = subscribe('ModuleConfigUpdated', () => {
      clearCapabilityConfig();
      setNonce(n => n + 1);
    });

    return () => { unsubscribe(); };
  }, [isLoggedIn, companyId, subscribe, subscribeToCompanyGroup]);

  const capabilities = useMemo<CompanyCapabilities>(() => {
    if (!loaded || denied.size === 0) return ALL_ALLOWED;
    return CAPABILITY_KEYS.reduce(
      (acc, key) => ({ ...acc, [key]: !denied.has(key) }),
      {} as CompanyCapabilities,
    );
  }, [loaded, denied]);

  const can = useCallback((key: CapabilityKey) => capabilities[key], [capabilities]);

  return useMemo(() => ({ capabilities, loaded, can }), [capabilities, loaded, can]);
}

export default useCompanyCapabilities;
